import { PageWrapper } from 'components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useGetMerchantQuery } from 'services/merchants';
import { MerchantPayoutFlow } from 'services/merchants/types';
import { pwcOrdersApi, useGetPwcOrderQuery } from 'services/pwcOrders';
import pwcRefundsApi from 'services/refunds';

import LoadingIndicator from 'components/loadingIndicator';

import RoutePath from 'router/path';

import { selectAccountAccess } from 'state/selectors/accounts/accountSelector';
import { selectUserLevel } from 'state/slices/userSlice';

import { AccountAccessLevel } from 'types/account';
import { APIError } from 'types/error';
import { UserAccessLevel } from 'types/user';

import { isRefundSupportedMerchantCurrency } from 'utils/refunds';

import { useRedirectOnAccountSwitch } from '../../utils/useRedirectOnAccountSwitch';
import { LABELS } from './keys';
import { OrderDetails } from './orderDetails';
import { hasUserS4cRole } from './utils';

export const PwcOrderViewContainer = () => {
  const { t } = useTranslation();
  useRedirectOnAccountSwitch(RoutePath.Pwc.Orders.Root);
  const [unlockOrderMutation, { isLoading: isUnlocking }] = pwcOrdersApi.useUnlockMutation();
  const [cancelOrderMutation, { isLoading: isCancelling }] = pwcOrdersApi.useCancelMutation();
  const [cancelRefundMutation, { isLoading: isCancellingRefund }] = pwcRefundsApi.useCancelMutation();

  const params = useParams();
  const { uuid } = params;

  const { data: orderPwc, isLoading } = useGetPwcOrderQuery(uuid!, {
    skip: !uuid,
  });

  const merchantUuid = orderPwc?.data?.merchant.uuid;

  const { data: merchantResponse } = useGetMerchantQuery(merchantUuid!, {
    skip: !merchantUuid,
  });

  const accountAccessLevel = useSelector(selectAccountAccess);
  const userLevel = useSelector(selectUserLevel);
  const isS4cUser = hasUserS4cRole(userLevel);
  const isSupport = userLevel === UserAccessLevel.Support;
  const isPartnerAdmin = [AccountAccessLevel.Owner, AccountAccessLevel.Administrator].includes(accountAccessLevel!);
  const payoutFlow = merchantResponse?.data?.payoutFlow;
  const isMerchantResolved = payoutFlow !== undefined && payoutFlow !== null;
  const isIndividualMerchant = payoutFlow === MerchantPayoutFlow.DIRECT;
  const isRefundMerchantCurrencySupported = isRefundSupportedMerchantCurrency(merchantResponse?.data?.mainFiatCurrency);

  const shouldShowExchangeDetailsSection = isS4cUser;
  // TODO: Change condition to show settlement details section when it's implemented
  const shouldShowSettlementDetailsSection = false;
  const shouldShowOrderDetailsSection = Boolean(orderPwc?.data?.general.items?.length);
  const shouldShowChargesListSection = Boolean(orderPwc?.data?.charges.length);
  const shouldShowOrderStatusHistorySection = Boolean(orderPwc?.data?.general.statusTransitionHistory?.length);

  const hasExtendedAccess = isS4cUser;

  const breadcrumbs = LABELS.BREADCRUMBS.map((item) => t(item));

  if (isLoading) {
    return (
      <PageWrapper pageTitle={t(LABELS.PAGE_TITLE)} breadcrumbs={breadcrumbs}>
        <LoadingIndicator padded />
      </PageWrapper>
    );
  }

  if (!orderPwc?.data) {
    return null;
  }

  const unlockOrder = async () => {
    if (!uuid) {
      throw new Error("Can't unlock order without order uuid");
    }

    return unlockOrderMutation({ uuid });
  };

  const cancelOrder = async () => {
    if (!uuid) {
      throw new Error("Can't cancel order without order uuid");
    }

    return cancelOrderMutation({ uuid });
  };

  const cancelRefund = async (orderUuid: string, refundUuid: string) => {
    if (!orderUuid || !refundUuid) {
      throw new Error("Can't cancel order without order and refund uuid");
    }

    return cancelRefundMutation({ orderUuid, refundUuid });
  };

  return (
    <PageWrapper breadcrumbs={breadcrumbs} pageTitle={t(LABELS.PAGE_TITLE)}>
      <OrderDetails
        orderPwcData={orderPwc?.data}
        shouldShowExchangeDetailsSection={shouldShowExchangeDetailsSection}
        hasExtendedAccess={hasExtendedAccess}
        shouldShowSettlementDetailsSection={shouldShowSettlementDetailsSection}
        shouldShowOrderDetailsSection={shouldShowOrderDetailsSection}
        shouldShowChargesListSection={shouldShowChargesListSection}
        shouldShowOrderStatusHistorySection={shouldShowOrderStatusHistorySection}
        isS4cUser={isS4cUser}
        isSupport={isSupport}
        isPartnerAdmin={isPartnerAdmin}
        isIndividualMerchant={isIndividualMerchant}
        isMerchantResolved={isMerchantResolved}
        isRefundMerchantCurrencySupported={isRefundMerchantCurrencySupported}
        unlock={{
          trigger: unlockOrder as () => Promise<{ error?: APIError }>,
          isLoading: isUnlocking,
        }}
        cancel={{
          trigger: cancelOrder as () => Promise<{ error?: APIError }>,
          isLoading: isCancelling,
        }}
        cancelRefund={{
          trigger: cancelRefund as (orderUuid: string, refundUuid: string) => Promise<{ error?: APIError }>,
          isLoading: isCancellingRefund,
        }}
      />
    </PageWrapper>
  );
};
